import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type Row = {
  id: string;
  user_id: string;
  role: "admin" | "user";
  email?: string | null;
  display_name?: string | null;
};

const AdminTeam = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [grantUserId, setGrantUserId] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("user_roles")
      .select("id, user_id, role, profiles:profiles!user_roles_user_id_fkey(display_name)")
      .order("created_at", { ascending: false });
    // The implicit FK between user_roles.user_id and profiles isn't always inferred,
    // fallback: separate query.
    if (error) {
      const fallback = await supabase.from("user_roles").select("id, user_id, role");
      setRows((fallback.data as any) ?? []);
    } else {
      setRows((data as any) ?? []);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const grant = async () => {
    const id = grantUserId.trim();
    if (!id) return;
    const { error } = await supabase.from("user_roles").insert({ user_id: id, role: "admin" });
    if (error) return toast.error(error.message);
    toast.success("Admin role granted");
    setGrantUserId("");
    load();
  };

  const revoke = async (rowId: string, userId: string) => {
    if (userId === user?.id) return toast.error("You can't revoke your own admin role");
    if (!confirm("Revoke admin access?")) return;
    const { error } = await supabase.from("user_roles").delete().eq("id", rowId);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl mb-2">Team & roles</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        General users can sign up and browse, but only admins can edit content. Grant editor access to a user case-by-case by pasting their User ID below.
        Have them sign up at <code className="bg-muted px-1.5 py-0.5 rounded">/auth</code> and share the ID shown on their "no access" screen.
      </p>

      <div className="bg-card rounded-2xl p-5 shadow-card mb-6">
        <h2 className="font-semibold mb-3">Grant admin access</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={grantUserId}
            onChange={(e) => setGrantUserId(e.target.value)}
            placeholder="User ID (UUID)"
            className="flex-1 px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm font-mono"
          />
          <button onClick={grant} className="px-5 py-2 rounded-full bg-primary text-primary-foreground font-semibold">Grant</button>
        </div>
      </div>

      <h2 className="font-semibold mb-3">Current admins</h2>
      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="grid gap-2">
          {rows.filter(r => r.role === "admin").map((r) => (
            <div key={r.id} className="bg-card rounded-xl p-4 flex items-center gap-3 shadow-card">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{(r as any).profiles?.display_name ?? "Admin"}</p>
                <p className="text-xs text-muted-foreground font-mono break-all">{r.user_id}</p>
              </div>
              {r.user_id === user?.id ? (
                <span className="text-xs px-3 py-1 rounded-full bg-primary text-primary-foreground">You</span>
              ) : (
                <button onClick={() => revoke(r.id, r.user_id)} className="text-sm font-semibold text-destructive">Revoke</button>
              )}
            </div>
          ))}
          {rows.filter(r => r.role === "admin").length === 0 && (
            <p className="text-muted-foreground text-sm">No admins yet.</p>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTeam;
